import React, { useEffect, useState, useCallback } from 'react';
import { formatCurrency, formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Loader2, ChevronLeft, ChevronRight, Calculator, AlertTriangle, Info, FileText, Edit2, Trash2, TrendingUp, Receipt, HelpCircle } from 'lucide-react';
import axios from 'axios';
import { useAuthStore } from '../store';

import { API_URL } from '../lib/api';

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const formatMonthDisplay = (monthStr) => {
  if (!monthStr) return '';
  const [year, month] = monthStr.split('-');
  return `${MONTHS[parseInt(month) - 1]} ${year}`;
};

const navigateMonth = (currentMonth, direction) => {
  const [year, month] = currentMonth.split('-').map(Number);
  let newMonth = month + direction;
  let newYear = year;
  if (newMonth < 1) { newMonth = 12; newYear--; }
  if (newMonth > 12) { newMonth = 1; newYear++; }
  return `${newYear}-${String(newMonth).padStart(2, '0')}`;
};

// Anexos do Simples Nacional
const SIMPLES_ANEXOS = [
  { value: 'I', label: 'Anexo I - Comércio', aliquota_inicial: '4%' },
  { value: 'II', label: 'Anexo II - Indústria', aliquota_inicial: '4,5%' },
  { value: 'III', label: 'Anexo III - Serviços (locação, etc)', aliquota_inicial: '6%' },
  { value: 'IV', label: 'Anexo IV - Construção, vigilância', aliquota_inicial: '4,5%' },
  { value: 'V', label: 'Anexo V - Serviços (tecnologia, etc)', aliquota_inicial: '15,5%' },
];

const initialFormData = {
  reference_month: getCurrentMonth(),
  gross_revenue: '',
  das_amount: '',
  das_due_date: '',
  das_paid_date: '',
  das_paid: false,
  simples_anexo: 'III',
  effective_rate: '',
  iss_retained: '',
  inss_retained: '',
  other_taxes: '',
  observations: ''
};

export const TaxesPage = () => {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [taxes, setTaxes] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedTax, setSelectedTax] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [summary, setSummary] = useState({ totalRevenue: 0, totalDAS: 0, totalRetained: 0 });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchTaxes = useCallback(async () => {
    setLoading(true);
    try {
      const year = selectedMonth.split('-')[0];
      const res = await axios.get(`${API_URL}/api/finance/taxes?year=${year}`, { headers });
      const data = Array.isArray(res.data) ? res.data : [];
      setTaxes(data);
      
      // Calculate summary
      const totalRevenue = data.reduce((sum, t) => sum + (t.gross_revenue || 0), 0);
      const totalDAS = data.reduce((sum, t) => sum + (t.das_amount || 0), 0);
      const totalRetained = data.reduce((sum, t) => sum + (t.iss_retained || 0) + (t.inss_retained || 0), 0);
      setSummary({ totalRevenue, totalDAS, totalRetained });
    } catch (error) {
      console.error('Error fetching taxes:', error);
      setTaxes([]);
      setSummary({ totalRevenue: 0, totalDAS: 0, totalRetained: 0 });
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, token]);

  useEffect(() => {
    fetchTaxes();
  }, [fetchTaxes]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/finance/taxes`, {
        ...formData,
        gross_revenue: parseFloat(formData.gross_revenue) || 0,
        das_amount: parseFloat(formData.das_amount) || 0,
        effective_rate: parseFloat(formData.effective_rate) || 0,
        iss_retained: parseFloat(formData.iss_retained) || 0,
        inss_retained: parseFloat(formData.inss_retained) || 0,
        other_taxes: parseFloat(formData.other_taxes) || 0,
      }, { headers });
      
      toast.success('Tributo registrado!');
      setIsCreateOpen(false);
      setFormData({ ...initialFormData, reference_month: selectedMonth });
      fetchTaxes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedTax) return;
    setIsSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/finance/taxes/${selectedTax.id}`, {
        ...formData,
        gross_revenue: parseFloat(formData.gross_revenue) || 0,
        das_amount: parseFloat(formData.das_amount) || 0,
        effective_rate: parseFloat(formData.effective_rate) || 0,
        iss_retained: parseFloat(formData.iss_retained) || 0,
        inss_retained: parseFloat(formData.inss_retained) || 0,
        other_taxes: parseFloat(formData.other_taxes) || 0,
      }, { headers });
      
      toast.success('Tributo atualizado!');
      setIsEditOpen(false);
      setSelectedTax(null);
      fetchTaxes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTax) return;
    setIsSubmitting(true);
    try {
      await axios.delete(`${API_URL}/api/finance/taxes/${selectedTax.id}`, { headers });
      toast.success('Registro excluído!');
      setIsDeleteOpen(false);
      setSelectedTax(null);
      fetchTaxes();
    } catch (error) {
      toast.error('Erro ao excluir');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditDialog = (tax) => {
    setSelectedTax(tax);
    setFormData({
      reference_month: tax.reference_month || '',
      gross_revenue: String(tax.gross_revenue || ''),
      das_amount: String(tax.das_amount || ''),
      das_due_date: tax.das_due_date || '',
      das_paid_date: tax.das_paid_date || '',
      das_paid: tax.das_paid || false,
      simples_anexo: tax.simples_anexo || 'III',
      effective_rate: String(tax.effective_rate || ''),
      iss_retained: String(tax.iss_retained || ''),
      inss_retained: String(tax.inss_retained || ''),
      other_taxes: String(tax.other_taxes || ''),
      observations: tax.observations || ''
    });
    setIsEditOpen(true);
  };

  const renderFormFields = () => (
    <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
      {/* Período */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Mês de Referência *</Label>
          <Input 
            type="month" 
            min="2026-01"
            value={formData.reference_month} 
            onChange={e => setFormData({...formData, reference_month: e.target.value})} 
          />
        </div>
        <div className="space-y-2">
          <Label>Anexo do Simples</Label>
          <Select value={formData.simples_anexo} onValueChange={v => setFormData({...formData, simples_anexo: v})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {SIMPLES_ANEXOS.map(a => (
                <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Separator />

      {/* Faturamento e DAS */}
      <div>
        <h4 className="font-medium mb-3 flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          Faturamento e DAS
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Faturamento Bruto (R$) *</Label>
            <Input 
              type="number" 
              step="0.01" 
              value={formData.gross_revenue} 
              onChange={e => setFormData({...formData, gross_revenue: e.target.value})} 
              placeholder="0,00"
            />
          </div>
          <div className="space-y-2">
            <Label>Valor DAS (R$)</Label>
            <Input 
              type="number" 
              step="0.01" 
              value={formData.das_amount} 
              onChange={e => setFormData({...formData, das_amount: e.target.value})} 
              placeholder="0,00"
            />
          </div>
          <div className="space-y-2">
            <Label>Alíquota Efetiva (%)</Label>
            <Input 
              type="number" 
              step="0.01" 
              value={formData.effective_rate} 
              onChange={e => setFormData({...formData, effective_rate: e.target.value})} 
              placeholder="Ex: 6.5"
            />
          </div>
          <div className="space-y-2">
            <Label>Vencimento DAS</Label>
            <Input 
              type="date" 
              value={formData.das_due_date} 
              onChange={e => setFormData({...formData, das_due_date: e.target.value})} 
            />
          </div>
          <div className="space-y-2">
            <Label>Data Pagamento DAS</Label>
            <Input 
              type="date" 
              value={formData.das_paid_date} 
              onChange={e => setFormData({...formData, das_paid_date: e.target.value, das_paid: !!e.target.value})} 
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Retenções */}
      <div>
        <h4 className="font-medium mb-3 flex items-center gap-2">
          <Receipt className="h-4 w-4" />
          Retenções na Fonte (quando aplicável)
        </h4>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>ISS Retido (R$)</Label>
            <Input 
              type="number" 
              step="0.01" 
              value={formData.iss_retained} 
              onChange={e => setFormData({...formData, iss_retained: e.target.value})} 
              placeholder="0,00"
            />
          </div>
          <div className="space-y-2">
            <Label>INSS Retido (R$)</Label>
            <Input 
              type="number" 
              step="0.01" 
              value={formData.inss_retained} 
              onChange={e => setFormData({...formData, inss_retained: e.target.value})} 
              placeholder="0,00"
            />
          </div>
          <div className="space-y-2">
            <Label>Outros Tributos (R$)</Label>
            <Input 
              type="number" 
              step="0.01" 
              value={formData.other_taxes} 
              onChange={e => setFormData({...formData, other_taxes: e.target.value})} 
              placeholder="0,00"
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Observações */}
      <div className="space-y-2">
        <Label>Observações</Label>
        <Textarea 
          value={formData.observations} 
          onChange={e => setFormData({...formData, observations: e.target.value})} 
          placeholder="Anotações relevantes sobre este período..."
          rows={3}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="taxes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">Tributos</h1>
            <span className="px-3 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
              Em Breve
            </span>
          </div>
          <p className="text-muted-foreground">Controle mensal de DAS e tributos - Simples Nacional</p>
        </div>
        <Button onClick={() => { setFormData({...initialFormData, reference_month: selectedMonth}); setIsCreateOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />Novo Registro
        </Button>
      </div>

      {/* Alerta sobre Transição Tributária */}
      <Alert className="border-amber-200 bg-amber-50">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-800">Transição Tributária em Andamento</AlertTitle>
        <AlertDescription className="text-amber-700">
          <p className="mb-2">
            A <strong>Reforma Tributária (EC 132/2023)</strong> está em fase de transição. O novo sistema <strong>IBS/CBS</strong> 
            substituirá gradualmente PIS, COFINS, ICMS, ISS e IPI entre 2026 e 2033.
          </p>
          <p className="mb-2">
            <strong>Para empresas do Simples Nacional:</strong> O regime simplificado será mantido, mas haverá adaptações. 
            Acompanhe as alterações constantes em curso.
          </p>
          <div className="mt-3 p-3 bg-white rounded border border-amber-200">
            <p className="font-medium text-amber-900 mb-1">📋 O que fazer agora:</p>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li>Mantenha o controle mensal do DAS normalmente</li>
              <li>Registre retenções de ISS e INSS quando houver</li>
              <li>Acompanhe comunicados do Simples Nacional</li>
            </ul>
          </div>
        </AlertDescription>
      </Alert>

      {/* Info sobre Simples Nacional */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Empresa no Simples Nacional - Pequeno Porte</AlertTitle>
        <AlertDescription>
          <p>
            O <strong>DAS (Documento de Arrecadação do Simples Nacional)</strong> é o guia único que engloba 
            todos os tributos federais, estaduais e municipais. Vencimento: dia 20 de cada mês subsequente.
          </p>
          <p className="mt-2 text-sm">
            Portal do Simples Nacional: www8.receita.fazenda.gov.br/SimplesNacional
          </p>
        </AlertDescription>
      </Alert>

      {/* Aviso sobre dados retroativos */}
      <Alert className="border-slate-200 bg-slate-50">
        <Info className="h-4 w-4 text-slate-600" />
        <AlertTitle className="text-slate-800">Controle a partir de 2026</AlertTitle>
        <AlertDescription className="text-slate-700">
          Este sistema registra apenas dados fiscais de <strong>Janeiro/2026 em diante</strong>. 
          Não é possível inserir registros retroativos de exercícios anteriores.
        </AlertDescription>
      </Alert>

      {/* Navigation */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <Button 
              variant="outline" 
              onClick={() => setSelectedMonth(navigateMonth(selectedMonth, -12))}
              disabled={parseInt(selectedMonth.split('-')[0]) <= 2026}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />{parseInt(selectedMonth.split('-')[0]) - 1}
            </Button>
            <div className="text-center">
              <h3 className="text-xl font-bold">{selectedMonth.split('-')[0]}</h3>
              <p className="text-sm text-muted-foreground">Exercício Fiscal</p>
            </div>
            <Button variant="outline" onClick={() => setSelectedMonth(navigateMonth(selectedMonth, 12))}>
              {parseInt(selectedMonth.split('-')[0]) + 1}<ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-sm text-muted-foreground">Faturamento Anual</p>
                <p className="text-xl font-bold font-mono">{formatCurrency(summary.totalRevenue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Calculator className="h-8 w-8 text-red-600" />
              <div>
                <p className="text-sm text-muted-foreground">Total DAS Pago</p>
                <p className="text-xl font-bold font-mono">{formatCurrency(summary.totalDAS)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Receipt className="h-8 w-8 text-orange-600" />
              <div>
                <p className="text-sm text-muted-foreground">Retenções</p>
                <p className="text-xl font-bold font-mono">{formatCurrency(summary.totalRetained)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-primary/5">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Carga Tributária</p>
                <p className="text-xl font-bold font-mono">
                  {summary.totalRevenue > 0 
                    ? ((summary.totalDAS + summary.totalRetained) / summary.totalRevenue * 100).toFixed(2) + '%'
                    : '0%'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Taxes List */}
      <Card>
        <CardHeader>
          <CardTitle>Registros Mensais - {selectedMonth.split('-')[0]}</CardTitle>
          <CardDescription>Controle de faturamento e tributos por mês</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}</div>
          ) : taxes.length === 0 ? (
            <div className="text-center py-12">
              <Calculator className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Nenhum registro para este ano</p>
              <Button className="mt-4" onClick={() => { setFormData({...initialFormData, reference_month: selectedMonth}); setIsCreateOpen(true); }}>
                <Plus className="h-4 w-4 mr-2" />Adicionar Registro
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Mês</TableHead>
                  <TableHead>Anexo</TableHead>
                  <TableHead className="text-right">Faturamento</TableHead>
                  <TableHead className="text-right">DAS</TableHead>
                  <TableHead className="text-right">Alíquota</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {taxes.sort((a,b) => a.reference_month.localeCompare(b.reference_month)).map(tax => (
                  <TableRow key={tax.id}>
                    <TableCell className="font-medium">{formatMonthDisplay(tax.reference_month)}</TableCell>
                    <TableCell><Badge variant="outline">Anexo {tax.simples_anexo || 'III'}</Badge></TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(tax.gross_revenue || 0)}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(tax.das_amount || 0)}</TableCell>
                    <TableCell className="text-right">{tax.effective_rate ? `${tax.effective_rate}%` : '-'}</TableCell>
                    <TableCell>
                      {tax.das_paid ? (
                        <Badge className="bg-green-100 text-green-800">Pago</Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-600 border-amber-300">Pendente</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => openEditDialog(tax)} title="Editar">
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => { setSelectedTax(tax); setIsDeleteOpen(true); }} title="Excluir">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Novo Registro de Tributos</DialogTitle>
            <DialogDescription>Registre o faturamento e DAS do mês</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            {renderFormFields()}
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Registro</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit}>
            {renderFormFields()}
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Registro</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o registro de <strong>{selectedTax && formatMonthDisplay(selectedTax.reference_month)}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TaxesPage;
